import {
  Add,
  Close,
  Delete,
  Edit,
  Image as ImageIcon,
  MoreVert,
  RefreshOutlined,
  Reply,
  SupportAgentOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import PopOutTextInput from "../../components/PopOutTextInput";
import { hostname, parseError } from "../../utils";
import SupportTicketDetail from "./SupportTicketDetail";
import StatusChip from "./components/StatusChip";
import TicketFormDialog, { SupportTicket } from "./dialogs/TicketFormDialog";

interface ResolveDialogProps {
  open: boolean;
  onClose: () => void;
  ticket: SupportTicket | null;
  getToken: () => Promise<string>;
  onResolve: () => void;
  onError: (error: string) => void;
}

const ResolveDialog: React.FC<ResolveDialogProps> = ({
  open,
  onClose,
  ticket,
  getToken,
  onResolve,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");

  useEffect(() => {
    setResponse("");
  }, [open]);

  const handleResolve = async () => {
    if (!response) {
      onError("Response is required to resolve a ticket");
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/support/tickets/${ticket?.id}/resolve`,
        { response },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onResolve();
      onClose();
    } catch (err) {
      onError(parseError(err));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Resolve Ticket
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {ticket && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {ticket.subject}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ticket.description}
            </Typography>
          </Box>
        )}
        <PopOutTextInput
          fullWidth
          label="Resolution Response"
          value={response}
          onChange={(value) => setResponse(value)}
          multiline
          rows={4}
          placeholder="Enter the resolution details..."
          required
          dialogTitle="Edit Resolution Response"
          dialogDescription="Provide detailed information about how the ticket was resolved."
          popOutRows={15}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          onClick={handleResolve}
          variant="contained"
          color="success"
        >
          Resolve
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default function Support({
  companyId,
  getToken,
  setError,
  setSuccess,
  companies,
  showConfirmDialog,
  isSuperUser = false,
}: {
  companyId?: string;
  getToken: () => Promise<string>;
  setError: (error: string) => void;
  setSuccess: (message: string) => void;
  companies: any[];
  showConfirmDialog?: (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => void;
  isSuperUser?: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null
  );
  const [screenshotViewerOpen, setScreenshotViewerOpen] = useState(false);
  const [viewingTicket, setViewingTicket] = useState<SupportTicket | null>(
    null
  );
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: ["open", "in_progress"] as string[],
    type: "all",
    severity: "all",
  });
  const [isWhitelabelOwner, setIsWhitelabelOwner] = useState(false);
  const [titleLoaded, setTitleLoaded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteConfirmTicketId, setDeleteConfirmTicketId] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [actionMenuTicket, setActionMenuTicket] = useState<SupportTicket | null>(
    null
  );

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuTicket(null);
  };

  const loadTickets = async (isRefresh = false, pageNum = page) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const token = await getToken();

      // Build query parameters for backend filtering
      const params = new URLSearchParams();
      // Send multiple status values as comma-separated string
      if (filters.status.length > 0 && filters.status.length < 4) {
        params.append("status", filters.status.join(","));
      }
      if (filters.type !== "all") params.append("type", filters.type);
      if (filters.severity !== "all")
        params.append("severity", filters.severity);

      // Add pagination parameters (backend uses 1-indexed pages)
      params.append("page", String(pageNum + 1));
      params.append("per_page", String(rowsPerPage));

      const url = `${hostname}/support/tickets${
        params.toString() ? "?" + params.toString() : ""
      }`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(response.data.tickets || []);
      if (response.data.pagination) {
        setTotalCount(response.data.pagination.total_count || 0);
      }
    } catch (err) {
      setError(parseError(err));
    }
    if (isRefresh) {
      setRefreshing(false);
    } else {
      setLoading(false);
    }

    // Check if we can end initial loading
    if (!isRefresh && titleLoaded) {
      setInitialLoading(false);
    }
  };

  const refreshTickets = () => {
    loadTickets(true);
  };

  useEffect(() => {
    setPage(0); // Reset to first page when filters change
    loadTickets(false, 0);
  }, [filters]); // Reload when filters change

  useEffect(() => {
    loadTickets();
  }, [page, rowsPerPage]); // Reload when pagination changes

  useEffect(() => {
    // Check if user is a whitelabel owner
    const checkWhitelabelOwnership = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/whitelabel/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const whitelabels = response.data.items || [];
        const hasOwnedWhitelabel = whitelabels.some((w: any) => w.is_owner);
        setIsWhitelabelOwner(hasOwnedWhitelabel);
      } catch (err) {
        // If whitelabel endpoint fails, assume not an owner
        setIsWhitelabelOwner(false);
      }
      setTitleLoaded(true);
      // Check if we can end initial loading
      if (!loading) {
        setInitialLoading(false);
      }
    };

    if (!isSuperUser) {
      checkWhitelabelOwnership();
    } else {
      // For super users, no need to check whitelabel ownership
      setTitleLoaded(true);
      // Check if we can end initial loading
      if (!loading) {
        setInitialLoading(false);
      }
    }
  }, [getToken, isSuperUser]);

  const performDelete = async (ticketId: string) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess("Ticket deleted successfully");
      loadTickets();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setDeleteConfirmTicketId(null);
    }
  };

  const handleDelete = async (ticketId: string) => {
    if (showConfirmDialog) {
      showConfirmDialog(
        "Delete Support Ticket",
        "Are you sure you want to delete this ticket? This action cannot be undone.",
        "Delete",
        () => performDelete(ticketId)
      );
    } else {
      // Show local confirm dialog
      setDeleteConfirmTicketId(ticketId);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  // Tickets are already filtered by the backend
  const filteredTickets = tickets;

  // Show detail view if a ticket is selected
  if (selectedDetailId) {
    return (
      <SupportTicketDetail
        getToken={getToken}
        setError={setError}
        setSuccess={setSuccess}
        ticketId={selectedDetailId}
        onBack={() => {
          setSelectedDetailId(null);
          refreshTickets();
        }}
        isSuperUser={isSuperUser}
        isWhitelabelOwner={isWhitelabelOwner}
      />
    );
  }

  // Show loading state while initial data is being fetched
  if (initialLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ pt: 2, pl: 4, pr: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4">
          {isSuperUser
            ? "Support Dashboard"
            : isWhitelabelOwner
            ? "Support Dashboard"
            : "Your Support Tickets"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedTicket(null);
            setFormDialogOpen(true);
          }}
        >
          New Ticket
        </Button>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={filters.status}
            label="Status"
            onChange={(e: SelectChangeEvent<string[]>) => {
              const value = e.target.value;
              setFilters((prev) => ({
                ...prev,
                status: typeof value === "string" ? value.split(",") : value,
              }));
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return "None selected";
              }
              if (selected.length === 4) {
                return "All Statuses";
              }
              return selected.map((s) => s.replace("_", " ")).join(", ");
            }}
          >
            <MenuItem value="open">
              <Checkbox checked={filters.status.indexOf("open") > -1} />
              <ListItemText primary="Open" />
            </MenuItem>
            <MenuItem value="in_progress">
              <Checkbox checked={filters.status.indexOf("in_progress") > -1} />
              <ListItemText primary="In Progress" />
            </MenuItem>
            <MenuItem value="resolved">
              <Checkbox checked={filters.status.indexOf("resolved") > -1} />
              <ListItemText primary="Resolved" />
            </MenuItem>
            <MenuItem value="closed">
              <Checkbox checked={filters.status.indexOf("closed") > -1} />
              <ListItemText primary="Closed" />
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filters.type}
            label="Type"
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, type: e.target.value }))
            }
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="bug">Bug</MenuItem>
            <MenuItem value="feature_request">Feature Request</MenuItem>
            <MenuItem value="question">Question</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={filters.severity}
            label="Severity"
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, severity: e.target.value }))
            }
          >
            <MenuItem value="all">All Severities</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>

        {(!(
          filters.status.length === 2 &&
          filters.status.includes("open") &&
          filters.status.includes("in_progress")
        ) ||
          filters.type !== "all" ||
          filters.severity !== "all") && (
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              setFilters({
                status: ["open", "in_progress"],
                type: "all",
                severity: "all",
              })
            }
          >
            Clear Filters
          </Button>
        )}
      </Box>

      <Paper sx={{ mt: 3, mb: 5 }} variant="outlined">
        {(refreshing || loading) && <LinearProgress />}
        {!refreshing && !loading && <div style={{ height: 4 }}></div>}

        <IconButton
          onClick={() => refreshTickets()}
          sx={{ float: "right", mr: 1 }}
          disabled={refreshing}
        >
          <RefreshOutlined />
        </IconButton>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Severity</TableCell>
                {(isSuperUser || isWhitelabelOwner) && (
                  <TableCell>User</TableCell>
                )}
                <TableCell>Created</TableCell>
                {!isSuperUser && !isWhitelabelOwner && (
                  <TableCell>Last Updated</TableCell>
                )}
                {(isSuperUser || isWhitelabelOwner) && (
                  <TableCell>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && filteredTickets.length === 0 ? (
                // Show loading state
                <TableRow>
                  <TableCell
                    colSpan={isSuperUser || isWhitelabelOwner ? 8 : 7}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <Typography color="text.secondary">
                      Loading support tickets...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredTickets.length === 0 ? (
                // Show empty state when no tickets
                <TableRow>
                  <TableCell
                    colSpan={isSuperUser || isWhitelabelOwner ? 8 : 7}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      {tickets.length === 0 ? (
                        <>
                          <SupportAgentOutlined
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 2,
                            }}
                          />
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            No support tickets yet
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                          >
                            Create your first support ticket to get help with
                            any issues or questions
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => {
                              setSelectedTicket(null);
                              setFormDialogOpen(true);
                            }}
                          >
                            Create Support Ticket
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            No tickets match your filters
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                          >
                            Try adjusting your filter criteria or clear all
                            filters
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              setFilters({
                                status: ["open", "in_progress"],
                                type: "all",
                                severity: "all",
                              })
                            }
                          >
                            Clear Filters
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                // Show actual tickets
                filteredTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSelectedDetailId(ticket.id)}
                  >
                    <TableCell>
                      <StatusChip status={ticket.status as any} />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {ticket.subject}
                            {ticket.attachments &&
                              ticket.attachments.length > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={
                                    isSuperUser || isWhitelabelOwner
                                      ? (e) => {
                                          e.stopPropagation();
                                          setViewingTicket(ticket);
                                          setScreenshotViewerOpen(true);
                                        }
                                      : undefined
                                  }
                                  sx={{ ml: 0.5, p: 0.5 }}
                                  title={
                                    isSuperUser || isWhitelabelOwner
                                      ? `View ${ticket.attachments.length} screenshot(s)`
                                      : `${ticket.attachments.length} screenshot(s)`
                                  }
                                >
                                  <ImageIcon
                                    fontSize="small"
                                    sx={{ color: "text.secondary" }}
                                  />
                                </IconButton>
                              )}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{
                              maxWidth:
                                isSuperUser || isWhitelabelOwner ? 300 : 400,
                            }}
                          >
                            {ticket.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ticket.companyName || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.ticketType
                          .replace("_", " ")
                          .toUpperCase()}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.severity.toUpperCase()}
                        size="small"
                        color={getSeverityColor(ticket.severity) as any}
                      />
                    </TableCell>
                    {(isSuperUser || isWhitelabelOwner) && (
                      <TableCell>
                        {ticket.userName || ticket.userEmail || "Unknown"}
                      </TableCell>
                    )}
                    <TableCell>
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </TableCell>
                    {!isSuperUser && !isWhitelabelOwner && (
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(ticket.updatedAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    )}
                    {(isSuperUser || isWhitelabelOwner) && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenuTicket(ticket);
                            setActionMenuAnchor(e.currentTarget);
                          }}
                          title="Actions"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        onClick={(e) => e.stopPropagation()}
      >
        {actionMenuTicket?.status === "open" && (
          <MenuItem
            onClick={() => {
              setSelectedTicket(actionMenuTicket);
              setResolveDialogOpen(true);
              closeActionMenu();
            }}
          >
            <ListItemIcon>
              <Reply fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resolve</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setSelectedTicket(actionMenuTicket);
            setFormDialogOpen(true);
            closeActionMenu();
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {isSuperUser && actionMenuTicket && (
          <MenuItem
            onClick={() => {
              const id = actionMenuTicket.id;
              closeActionMenu();
              handleDelete(id);
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <TicketFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        ticket={selectedTicket}
        companies={companies}
        getToken={getToken}
        onSave={() => {
          setSuccess(selectedTicket ? "Ticket updated" : "Ticket created");
          loadTickets();
        }}
        onError={setError}
      />

      <ResolveDialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        ticket={selectedTicket}
        getToken={getToken}
        onResolve={() => {
          setSuccess("Ticket resolved");
          loadTickets();
        }}
        onError={setError}
      />

      {/* Screenshot Viewer Dialog - Super Users and Whitelabel Owners */}
      {(isSuperUser || isWhitelabelOwner) && (
        <Dialog
          open={screenshotViewerOpen}
          onClose={() => setScreenshotViewerOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Screenshots for: {viewingTicket?.subject}
            <IconButton
              onClick={() => setScreenshotViewerOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {viewingTicket?.attachments &&
            viewingTicket.attachments.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                }}
              >
                {viewingTicket.attachments.map((imageUrl, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={/^https?:\/\/[a-zA-Z0-9]/.test(imageUrl) ? imageUrl : ""}
                      alt={`Screenshot ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        maxHeight: "400px",
                        objectFit: "contain",
                      }}
                    />
                    <Box sx={{ p: 1, bgcolor: "background.paper" }}>
                      <Typography variant="caption" color="text.secondary">
                        Screenshot {index + 1}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography>No screenshots available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScreenshotViewerOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog (fallback when showConfirmDialog is not available) */}
      <Dialog
        open={deleteConfirmTicketId !== null}
        onClose={() => setDeleteConfirmTicketId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Support Ticket</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this ticket? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmTicketId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmTicketId && performDelete(deleteConfirmTicketId)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
